import { SearchOption } from "./PlaceSearchWithSavedLocations";
import { CustomerDeliveryLocation } from "dora-contracts";
import { useState } from "react";
import {
  Place,
  StarBorder,
  Apartment,
  ChevronRight,
  ChevronLeft,
  Add,
  Star,
} from "@mui/icons-material";
import HomeIcon from "@mui/icons-material/Home";
import SaveIcon from "@mui/icons-material/Save";
import "./SearchResultOption.scss";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { AutoSuggestItem, ParsedText } from "dora-shared";
import match from "autosuggest-highlight/match";
import RenderParsedText from "../modals/common/location-search-result/RenderParsedText";

// Build a ParsedText so matching parts of the input are highlighted, the same
// way search-result options highlight their matches.
const toParsedText = (label: string, query: string): ParsedText => ({
  label,
  highlights: match(label, query).map(([start, end]) => ({ start, end })),
});

const SearchResultOption = ({
  option,
  onBookmarkClick,
  onSaveDeliveryAddressClick,
  inputValue,
}: {
  option: SearchOption;
  onBookmarkClick: (location: SearchOption) => void;
  onSaveDeliveryAddressClick?: (location: SearchOption) => void;
  inputValue: string;
}) => {
  const { t } = useTranslation("modals");
  const { type, value } = option;
  const [showBookmark, setShowBookmark] = useState(false);

  let component;

  switch (type) {
    case "TERMINALS_MENU":
      component = (
        <div className="search-result-option__wrapper">
          <Apartment className="search-result-option__icon" />
          <div className="search-result-option__text">
            {t("manageSavedLocations.terminals")}
          </div>
          <ChevronRight className="search-result-option__chevron" />
        </div>
      );
      break;
    case "TERMINALS_BACK":
      component = (
        <div className="search-result-option__wrapper">
          <ChevronLeft className="search-result-option__chevron" />
          <div className="search-result-option__text">
            {t("manageSavedLocations.terminals")}
          </div>
        </div>
      );
      break;
    case "ADD_TERMINAL":
      component = (
        <div className="search-result-option__wrapper">
          <Add className="search-result-option__icon" />
          <div className="search-result-option__terminal-text">
            {t("manageSavedLocations.addTerminal")}
          </div>
        </div>
      );
      break;
    case "CUSTOMER_DELIVERY_ADDRESS": {
      const location = value.data as CustomerDeliveryLocation;
      // Show the Dora name on the first row, falling back to the address for
      // locations saved before the name field existed.
      let title = location.name || location.address || "";
      let secondary = [location.postalCode, location.city, location.country]
        .filter(Boolean)
        .join(", ");

      if (!title) {
        title = secondary;
        secondary = "";
      }

      component = (
        <div className="search-result-option__wrapper">
          <HomeIcon className="search-result-option__icon" />
          <div className="search-result-option__text">
            <div>
              <RenderParsedText item={toParsedText(title, inputValue)} />
            </div>
            {secondary && (
              <Typography variant="h4" color="text.secondary">
                <RenderParsedText
                  item={toParsedText(secondary, inputValue)}
                  secondary
                />
              </Typography>
            )}
          </div>
        </div>
      );
      break;
    }
    default:
      switch (value.locationType) {
        case "TERMINAL":
          component = (
            <div className="search-result-option__wrapper">
              <Apartment className="search-result-option__icon" />
              <div className="search-result-option__text">
                {value.data.customName}
              </div>
            </div>
          );
          break;
        case "FAVORITE":
          component = (
            <div className="search-result-option__wrapper">
              <Star className="search-result-option__icon" />
              <div className="search-result-option__text">
                {value.data.customName}
              </div>
            </div>
          );
          break;
        default:
          const data = value.data as AutoSuggestItem;
          component = (
            <div className="search-result-option__wrapper">
              <Place className="search-result-option__icon" />
              <div className="search-result-option__text">
                <div>
                  <RenderParsedText item={data.title} />
                </div>
                <Typography variant="h4" color="text.secondary">
                  <RenderParsedText item={data.address} secondary />
                </Typography>
              </div>
              {showBookmark &&
                "canSaveDeliveryAddress" in value &&
                value.canSaveDeliveryAddress && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      e.nativeEvent.stopPropagation();
                      e.nativeEvent.preventDefault();
                      onSaveDeliveryAddressClick?.(option);
                    }}
                    style={{ marginRight: 6 }}
                  >
                    <SaveIcon style={{ fontSize: 19 }} className="search-result-option__bookmark" />
                  </div>
                )}
              {showBookmark && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    e.nativeEvent.stopPropagation();
                    e.nativeEvent.preventDefault();
                    onBookmarkClick(option);
                  }}
                >
                  <StarBorder className="search-result-option__bookmark" />
                </div>
              )}
            </div>
          );
          break;
      }
  }

  return (
    <div
      onMouseEnter={() => setShowBookmark(true)}
      onMouseLeave={() => setShowBookmark(false)}
      className="search-result-option__container"
    >
      {component}
    </div>
  );
};

export default SearchResultOption;
